import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Hammer, Spinner } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("info@clickforgeds.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to the forge.");
      const dest = location.state?.from?.pathname || "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-950 text-zinc-100">
      {/* Left visual */}
      <div className="hidden md:block relative overflow-hidden grain border-r border-zinc-900">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/reserve/7vjJbdDRga27ApDoYicw_Sparks.jpg?crop=entropy&cs=srgb&fm=jpg&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950 via-zinc-950/80 to-orange-950/30" />
        <div className="relative h-full flex flex-col justify-between p-10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
              <Hammer size={20} weight="bold" className="text-zinc-950" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">ClickForge</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-400">
                Digital Solutions
              </div>
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-400 mb-3">
              Lead Generation Agent
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-semibold leading-tight tracking-tight max-w-md">
              Forge a pipeline of <span className="text-orange-500">Midwest</span> business owners ready to buy.
            </h1>
            <p className="text-zinc-400 text-sm mt-4 max-w-md leading-relaxed">
              AI-powered prospecting, scoring, audits, and outreach — built for ClickForge Digital Solutions.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-500 mb-2">
              Welcome back
            </div>
            <h2 className="font-display text-3xl font-semibold text-zinc-50">Sign in</h2>
            <p className="text-sm text-zinc-500 mt-1">
              No account?{" "}
              <Link to="/signup" data-testid="login-go-signup-link" className="text-orange-400 hover:text-orange-300 underline-offset-2 hover:underline">
                Create one
              </Link>
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 bg-zinc-900 border-zinc-800 focus-visible:border-orange-500 focus-visible:ring-orange-500/30"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password-link"
                  className="text-[10px] font-mono uppercase tracking-wider text-orange-400 hover:text-orange-300"
                >
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 bg-zinc-900 border-zinc-800 focus-visible:border-orange-500 focus-visible:ring-orange-500/30"
              />
            </div>

            {error ? (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-sm px-3 py-2">
                {error}
              </div>
            ) : null}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 font-medium rounded-sm h-10"
            >
              {loading ? (<><Spinner size={16} className="mr-2 animate-spin" /> Signing in…</>) : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
